import { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { investingApi } from "../../services/api";
import { parseApiError } from "../../services/apiError";
import { revealLater } from "./lib/reveal";
import useSubmitGuard from "../../hooks/useSubmitGuard";

const MAIN = "h-11 md:h-9";
const REASON_MAX = 1000; // backend/investing/constants.py

/**
 * Retract a research note (spec §2.1: a person's, in their own browser —
 * the page shows this only to a person, and the server refuses everyone
 * else with a 403, shown as it says). A reason is required: it's shown
 * with the note. The note is kept but hidden from the company's page, and
 * its scores leave the company's current scores.
 *
 * `onRetracted(page)` gets the company's page as the API returned it.
 * Static backdrop; Escape and Cancel are refused while it runs.
 */
function RetractNoteDialog({ open, noteId, symbol, onOpenChange, onRetracted, onCloseAutoFocus }) {
  const id = useId();
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const reasonRef = useRef(null);
  const errorRef = useRef(null);
  const submitGuard = useSubmitGuard(open);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setReasonError(null);
    setError(null);
    setBusy(false);
  }, [open]);

  const retract = async (event) => {
    event.preventDefault();
    // No busy check: the submit button is disabled while it runs.
    const why = reason.trim();
    if (!why) {
      setReasonError("Say why — it's shown with the note.");
      reasonRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await investingApi.notes.retract(noteId, why);
      if (mounted.current) setBusy(false);
      onRetracted(data);
    } catch (err) {
      if (!mounted.current) return;
      setBusy(false);
      const parsed = parseApiError(err, "Couldn't retract the note.");
      if (parsed.fieldErrors.reason) {
        setReasonError([].concat(parsed.fieldErrors.reason).join(" "));
        // Once it's enabled again (it's disabled while busy).
        setTimeout(() => reasonRef.current?.focus(), 0);
      } else {
        revealLater(errorRef);
      }
      const other = Object.entries(parsed.fieldErrors)
        .filter(([name]) => name !== "reason")
        .map(([name, value]) => `${name}: ${[].concat(value).join(" ")}`);
      setError([parsed.formError, ...other].filter(Boolean).join(" — ") || null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => onOpenChange(next)}>
      <DialogContent
        data-investing=""
        showCloseButton={false}
        className="flex max-h-[85dvh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          reasonRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl">Retract note #{noteId}?</DialogTitle>
          <DialogDescription>
            It&apos;s hidden from {symbol}&apos;s page (Show retracted brings it back into view), and its
            scores leave {symbol}&apos;s current scores — each thesis falls back to the newest note left.
            The note itself is kept, with your reason.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={retract} noValidate className="flex min-h-0 flex-1 flex-col">
          <div data-slot="dialog-body" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-reason`}>Reason</Label>
              <Textarea
                ref={reasonRef}
                id={`${id}-reason`}
                value={reason}
                maxLength={REASON_MAX}
                placeholder="e.g. It quotes another company's filing"
                className="max-h-48 min-h-20"
                disabled={busy}
                aria-invalid={reasonError ? true : undefined}
                aria-describedby={`${id}-reason-help${reasonError ? ` ${id}-reason-error` : ""}`}
                onChange={(event) => {
                  setReason(event.target.value);
                  setReasonError(null);
                }}
              />
              <p id={`${id}-reason-help`} className="m-0 text-xs text-muted-foreground">
                Required. Shown with the note.
              </p>
              {reasonError && (
                <p id={`${id}-reason-error`} className="m-0 text-sm text-destructive">
                  {reasonError}
                </p>
              )}
            </div>
            {error && (
              <Alert ref={errorRef} variant="destructive" role="alert" tabIndex={-1} className="outline-none">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" className={MAIN} disabled={busy} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" className={MAIN} disabled={busy} {...submitGuard}>
              {busy && <Loader2 className="animate-spin" aria-hidden="true" />}
              {busy ? `Retracting note #${noteId}…` : `Retract note #${noteId}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

RetractNoteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  noteId: PropTypes.number,
  symbol: PropTypes.string.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  /** `(page)`: retracted; the company's page as the API returned it. */
  onRetracted: PropTypes.func.isRequired,
  /** Radix's close autofocus: the page moves focus when the opener went away. */
  onCloseAutoFocus: PropTypes.func,
};

export default RetractNoteDialog;
